<script lang="ts" setup>
import { formatNumber, formatPrice } from '@/helper/Translations'
import FormElement from '@/components/FormElement.vue'
import { filterUserPriceInput } from '@/helper/filter'
import { computed, ref } from 'vue'
import UserStore from '@/store/UserStore'

const props = defineProps<{
  price?: number
  hasReset?: boolean
  placeholder?: string
  error?: string
  id: string
  originalPrice?: number
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:price', value: string | number | undefined): void
  (e: 'blur'): void
}>()

const input = ref<HTMLInputElement | null>(null)

const inputVal = computed(() => {
  return props.price === undefined ? formatNumber(props.originalPrice, 2, false) : formatNumber(props.price, 2, false)
})

/**
 * If input delete/cleared or set to original price reset the value by sending undefined
 * If input is not a number do not send the update
 */
function inputBlurred(): void {
  if (input.value) {
    const val = input.value?.value
    const sanitizedVal = filterUserPriceInput(val)
    const parsedVal = parseFloat(sanitizedVal)
    if (isNaN(parsedVal) && val.trim() !== '') {
      return
    }
    const resetChange = val.trim() === '' || parsedVal === props.originalPrice
    emitChange(resetChange ? undefined : parsedVal)
  }
}

function emitChange(val: number | undefined): void {
  emit('update:price', val)
  emit('blur')
}

function resetPrice(): void {
  emitChange(undefined)
}
</script>

<template>
  <div class="price-input">
    <div class="price-input__formatted-price">
      <span>{{ formatPrice(price !== undefined ? price : originalPrice, UserStore.currentIsoLocale) }}</span>
    </div>
    <FormElement :error="error">
      <input
        :id="id"
        ref="input"
        v-model="inputVal"
        :class="{ 'is--filled': inputVal !== '' }"
        :disabled="disabled"
        :placeholder="placeholder || $t('step_accessories.placeholder_price')"
        class="price-input__input"
        type="text"
        @blur="inputBlurred"
      >
      <button v-if="price" class="icon-btn input__reset" @click="resetPrice">
        <i class="ic-reset" />
      </button>
    </FormElement>
  </div>
</template>

<style lang="scss" scoped>
.price-input {
  position: relative;
  margin-bottom: 0;
  margin-right: rem-calc(8);

  &__formatted-price {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: rem-calc($inputHeight);
    display: flex;
    align-items: center;
    padding: 0 rem-calc($inputVerticalPadding + 1); // 1px = border
    z-index: 0;
    background: #fff;

    @include themify($themes) {
      border-radius: rem-calc(themed('inputBorderRadius'));
    }

    span {
      font-size: rem-calc($textSize);

      .package__badge & {
        font-size: rem-calc($textLargeSize);
      }
    }
  }

  &__input {
    position: relative;
    z-index: 1;
    line-height: rem-calc($inputHeight);
    width: 100%;

    &:not(:focus) {
      color: transparent;
      background: transparent;
      z-index: 0;
    }

    &:not(.is--filled) {
      color: unset;

      & + .price-input__reset {
        display: none;
      }
    }
  }

  .form__element {
    margin-bottom: 0;
    width: 100%;
    z-index: 1;
  }
}
</style>
